import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, Collapse, Divider, IconButton, LinearProgress, Paper, Stack, Switch, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import {
  ContentCopy, ExpandLess, ExpandMore, LanguageOutlined, OpenInNew, PauseCircleOutline, PlayCircleOutline,
  SchoolOutlined, StorefrontOutlined, LockOutlined,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useCopyLink } from '../../../hooks/useCopyLink';
import { admissionCycleService } from '../../../services/admissions/admissionCycleService';
import { AdmissionCycle, AdmissionIntake, SeatSnapshot } from '../../../types/admissions';
import { ToneChip } from '../../common/StatusChip';
import ConfirmationDialog, { ConfirmFacts } from '../../common/ConfirmationDialog';
import { fmtClassName } from '../../../utils/pdf';
import { STOP_CONFIRM, intakeHeadline, onlineLine, stopClassConfirm, walkInLine } from './intakeCopy';

interface Props {
  cycle: AdmissionCycle;
  /** May change intake and the cycle's lifecycle. */
  canManage: boolean;
  onChanged: (cycle: AdmissionCycle) => void;
  onReopen: () => void;
}

const idOf = (c: any) => String(c?._id ?? c);

/** A change waiting for the operator to confirm it. */
interface PendingStop {
  key: string;
  change: Partial<AdmissionIntake>;
  done: string;
  title: string;
  message: string;
  confirmText: string;
  facts?: Array<{ label: string; value: string }>;
}

/**
 * New applications, separate from the cycle's lifecycle: switch the online
 * form and walk-ins on or off, or stop a full class, without ending the
 * season. Applicants already in the cycle are processed either way.
 */
const CycleIntakeCard: React.FC<Props> = ({ cycle, canManage, onChanged, onReopen }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const copy = useCopyLink();
  const [saving, setSaving] = useState<string | null>(null);
  const [classesOpen, setClassesOpen] = useState(false);
  const [seats, setSeats] = useState<SeatSnapshot[] | null>(null);
  const [pending, setPending] = useState<PendingStop | null>(null);

  const classes = useMemo(
    () => (cycle.allowedClassIds || []).map((c: any) => ({ _id: idOf(c), name: typeof c === 'object' ? c.name : String(c) })),
    [cycle.allowedClassIds],
  );
  const closed = useMemo(() => new Set((cycle.intake?.closedClassIds || []).map(idOf)), [cycle.intake]);
  const openClasses = classes.filter(c => !closed.has(c._id)).length;

  useEffect(() => {
    if (!classesOpen || seats) return;
    admissionCycleService.seatMatrix(cycle._id)
      .then(setSeats)
      .catch((err: any) => enqueueSnackbar(err?.message || 'Could not load the seats', { variant: 'error' }));
  }, [classesOpen, seats, cycle._id, enqueueSnackbar]);

  if (cycle.status === 'Archived') return null;

  const save = async (key: string, change: Partial<AdmissionIntake>, done: string) => {
    setSaving(key);
    try {
      const next = await admissionCycleService.setIntake(cycle._id, change);
      onChanged(next);
      setPending(null);
      enqueueSnackbar(done, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not save', { variant: 'error' });
    } finally {
      setSaving(null);
    }
  };

  // Stopping intake on a live cycle reaches parents and the desk, so it is
  // confirmed first; turning anything back on, or setting up a draft, isn't.
  const stop = (next: PendingStop) => (cycle.status === 'Open' ? setPending(next) : save(next.key, next.change, next.done));

  const toggleClass = (classId: string, taking: boolean) => {
    const current = Array.from(closed);
    const name = fmtClassName(classes.find(c => c._id === classId)?.name ?? '');
    const key = `class:${classId}`;
    if (taking) {
      save(key, { closedClassIds: current.filter(id => id !== classId) }, `${name} is taking applications again`);
      return;
    }
    const s = seats?.find(x => x.classId === classId);
    stop({
      key,
      change: { closedClassIds: [...current, classId] },
      done: `${name} stopped taking applications`,
      ...stopClassConfirm(name),
      facts: s ? [{ label: 'Seats offered', value: `${s.live} of ${s.totalSeats}` }, { label: 'Seats left', value: s.remaining > 0 ? String(s.remaining) : 'None — full' }] : undefined,
    });
  };

  const headline = intakeHeadline(cycle);
  const anyOn = cycle.intake.online || cycle.intake.walkIn;
  const isClosed = cycle.status === 'Closed';
  const publicUrl = cycle.publicToken ? admissionCycleService.publicApplyUrl(cycle.publicToken) : null;
  const editable = canManage && !isClosed;

  const copyLink = async () => {
    if (!publicUrl) return;
    await copy(publicUrl, 'Apply link');
  };

  const row = (icon: React.ReactNode, title: string, line: string, control: React.ReactNode, extra?: React.ReactNode, muted?: boolean) => (
    <Stack direction="row" spacing={1.75} alignItems="center" sx={{ px: 2.5, py: 1.75 }}>
      <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'grid', placeItems: 'center', flexShrink: 0, bgcolor: alpha(theme.palette.primary.main, muted ? 0.04 : 0.08), color: muted ? 'text.disabled' : 'primary.main' }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>{title}</Typography>
        <Typography variant="caption" color="text.secondary" component="div">{line}</Typography>
      </Box>
      {extra}
      {control}
    </Stack>
  );

  const channelSwitch = (key: 'online' | 'walkIn', label: string) => (
    <Tooltip title={!canManage ? 'Only people who manage admission settings can change this' : isClosed ? 'Reopen the cycle to take applications' : ''}>
      <span>
        {saving === key ? <CircularProgress size={20} sx={{ m: 1.25 }} /> : (
          <Switch
            checked={cycle.intake[key]}
            disabled={!editable || !!saving}
            onChange={e => (e.target.checked
              ? save(key, { [key]: true }, `${label} resumed`)
              : stop({ key, change: { [key]: false }, done: `${label} ${key === 'online' ? 'paused' : 'stopped'}`, ...STOP_CONFIRM[key] }))}
            inputProps={{ 'aria-label': label }}
          />
        )}
      </span>
    </Tooltip>
  );

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, mb: 3, overflow: 'hidden' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} sx={{ px: 2.5, py: 1.75, bgcolor: alpha(theme.palette.text.primary, 0.015), borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={700}>New applications</Typography>
            {headline && <ToneChip label={headline.label} tone={headline.tone} />}
            {isClosed && <ToneChip label="Cycle closed" tone="neutral" icon={LockOutlined} />}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {isClosed
              ? 'The season has ended — no new applications. Applicants already in the cycle are still processed.'
              : cycle.status === 'Draft'
                ? 'Choose how applications will come in once the cycle opens.'
                : 'Pause or resume without ending the cycle. Applicants already in are processed either way.'}
          </Typography>
        </Box>
        {isClosed && canManage && (
          <Button variant="contained" disableElevation size="small" startIcon={<PlayCircleOutline />} onClick={onReopen}>Reopen cycle</Button>
        )}
        {cycle.status === 'Open' && canManage && (anyOn ? (
          <Button
            variant="outlined" color="warning" size="small" startIcon={<PauseCircleOutline />} disabled={!!saving}
            onClick={() => stop({ key: 'all', change: { online: false, walkIn: false }, done: 'New applications stopped', ...STOP_CONFIRM.all })}
          >
            Stop all new applications
          </Button>
        ) : (
          <Button
            variant="contained" disableElevation size="small" startIcon={<PlayCircleOutline />} disabled={!!saving}
            onClick={() => save('all', { online: true, walkIn: true }, 'New applications resumed')}
          >
            Resume
          </Button>
        ))}
      </Stack>

      {row(<LanguageOutlined fontSize="small" />, 'Online form', onlineLine(cycle), channelSwitch('online', 'Online applications'),
        publicUrl && cycle.status === 'Open' ? (
          <Stack direction="row" spacing={0.25} sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <Tooltip title="Copy apply link"><IconButton size="small" onClick={copyLink} aria-label="Copy apply link"><ContentCopy fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Open the form as parents see it">
              <IconButton size="small" component="a" href={publicUrl} target="_blank" rel="noopener noreferrer" aria-label="Open the public form"><OpenInNew fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
        ) : undefined,
        !cycle.intakeNow.online.open)}
      <Divider />
      {row(<StorefrontOutlined fontSize="small" />, 'Walk-ins & bulk upload', walkInLine(cycle), channelSwitch('walkIn', 'Walk-ins'), undefined, !cycle.intakeNow.walkIn.open)}
      <Divider />
      {row(
        <SchoolOutlined fontSize="small" />,
        'Classes',
        classes.length === 0 ? 'No classes in this cycle yet'
          : openClasses === classes.length ? `All ${classes.length} class${classes.length === 1 ? '' : 'es'} taking applications`
          : openClasses === 0 ? 'No class is taking applications'
          : `${openClasses} of ${classes.length} classes taking applications`,
        <Button size="small" endIcon={classesOpen ? <ExpandLess /> : <ExpandMore />} onClick={() => setClassesOpen(o => !o)} disabled={!classes.length}>
          {classesOpen ? 'Hide' : editable ? 'Manage' : 'Show'}
        </Button>,
        undefined,
        openClasses === 0,
      )}
      <Collapse in={classesOpen} unmountOnExit>
        <Box sx={{ px: 2.5, pb: 1.5, pl: { sm: 9.5 } }}>
          {!seats && <LinearProgress sx={{ my: 1.5 }} />}
          {seats && classes.map(c => {
            const s = seats.find(x => x.classId === c._id);
            const taking = !closed.has(c._id);
            const full = !!s && s.remaining <= 0;
            return (
              <Stack key={c._id} direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.75, borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} color={taking ? 'text.primary' : 'text.secondary'}>{fmtClassName(c.name)}</Typography>
                  {s && (
                    <Typography variant="caption" color={full ? 'warning.main' : 'text.secondary'}>
                      {s.live} of {s.totalSeats} seats offered{full ? ' — full' : ` · ${s.remaining} left`}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {taking ? 'Taking applications' : 'Not taking applications'}
                </Typography>
                {saving === `class:${c._id}` ? <CircularProgress size={20} sx={{ m: 1.25 }} /> : (
                  <Switch size="small" checked={taking} disabled={!editable || !!saving} onChange={e => toggleClass(c._id, e.target.checked)} inputProps={{ 'aria-label': `${c.name} taking applications` }} />
                )}
              </Stack>
            );
          })}
        </Box>
      </Collapse>
      {pending && (
        <ConfirmationDialog
          open
          onClose={() => { if (!saving) setPending(null); }}
          onConfirm={() => save(pending.key, pending.change, pending.done)}
          title={pending.title}
          message={pending.message}
          confirmText={pending.confirmText}
          confirmColor="warning"
          loading={saving === pending.key}
        >
          {pending.facts && <ConfirmFacts items={pending.facts} />}
        </ConfirmationDialog>
      )}
    </Paper>
  );
};

export default CycleIntakeCard;
